/**
 * Story 049 — factory default Dashboard layout.
 *
 * Locked by UX brief §4. The `lg` numbers mirror the story's §"Default
 * layout" sketch verbatim:
 *   KPIs across the top row (h=4 each), Latest Inspections w=8 h=10
 *   bottom-left, Recent Activity w=4 h=10 bottom-right.
 *
 * The four breakpoints (lg / md / sm / xs) match the AnalysisWorkspace
 * grid params so a user's muscle memory transfers between the two
 * surfaces. `compactType: "vertical"` stacks any newly-restored widget
 * below the visible stack at every breakpoint.
 */
import type { Layout } from "react-grid-layout";

export type WidgetKey =
  | "kpi-open-inspections"
  | "kpi-defects-this-month"
  | "latest-inspections"
  | "recent-activity"
  | "fleet-map"
  | "ongoing-projects";

/**
 * Display title for each widget. Used by `<Paper aria-label>`,
 * `aria-label="Move {title}"` on the drag handle, and
 * `aria-label="Hide {title}"` on the close button. Acceptance test
 * AC1 reads these names back via `getByRole("region", { name: …})`.
 */
export const WIDGET_TITLE: Record<WidgetKey, string> = {
  "kpi-open-inspections": "Open Inspections",
  "kpi-defects-this-month": "Defects this month",
  "latest-inspections": "Latest inspections",
  "recent-activity": "Recent activity",
  "fleet-map": "Fleet Map",
  "ongoing-projects": "Ongoing Projects",
};

export const ALL_WIDGETS: readonly WidgetKey[] = [
  "kpi-open-inspections",
  "kpi-defects-this-month",
  "latest-inspections",
  "recent-activity",
  "fleet-map",
  "ongoing-projects",
];

/**
 * The exact `lg` shape the AC2 selector pins on:
 *   Open Inspections at x=0, y=0.
 *
 * Don't reorder the `lg` array — RGL doesn't care about array order, but
 * keeping the visual reading order matches what humans expect when
 * grepping the file.
 */
export const DEFAULT_LAYOUTS: Record<string, Layout[]> = {
  lg: [
    { i: "kpi-open-inspections", x: 0, y: 0, w: 6, h: 4, minW: 3, minH: 3 },
    { i: "kpi-defects-this-month", x: 6, y: 0, w: 6, h: 4, minW: 3, minH: 3 },
    // ONGOING-2 — the two-pane card wants horizontal room; sit it high in the
    // glanceable zone (just under the KPI row), wide like latest-inspections.
    { i: "ongoing-projects", x: 0, y: 4, w: 8, h: 12, minW: 5, minH: 8 },
    { i: "latest-inspections", x: 0, y: 16, w: 8, h: 10, minW: 4, minH: 6 },
    { i: "recent-activity", x: 8, y: 16, w: 4, h: 10, minW: 3, minH: 6 },
    // MAP-4 — the Fleet Map wants room; full-width below the rest at lg.
    { i: "fleet-map", x: 0, y: 26, w: 8, h: 14, minW: 5, minH: 8 },
  ],
  md: [
    { i: "kpi-open-inspections", x: 0, y: 0, w: 5, h: 4, minW: 3, minH: 3 },
    { i: "kpi-defects-this-month", x: 5, y: 0, w: 5, h: 4, minW: 3, minH: 3 },
    { i: "ongoing-projects", x: 0, y: 4, w: 10, h: 12, minW: 5, minH: 8 },
    { i: "latest-inspections", x: 0, y: 16, w: 7, h: 10, minW: 4, minH: 6 },
    { i: "recent-activity", x: 7, y: 16, w: 3, h: 10, minW: 3, minH: 6 },
    { i: "fleet-map", x: 0, y: 26, w: 10, h: 12, minW: 5, minH: 8 },
  ],
  sm: [
    { i: "kpi-open-inspections", x: 0, y: 0, w: 3, h: 4 },
    { i: "kpi-defects-this-month", x: 3, y: 0, w: 3, h: 4 },
    { i: "ongoing-projects", x: 0, y: 4, w: 6, h: 12 },
    { i: "latest-inspections", x: 0, y: 16, w: 6, h: 8 },
    { i: "recent-activity", x: 0, y: 24, w: 6, h: 8 },
    { i: "fleet-map", x: 0, y: 32, w: 6, h: 12 },
  ],
  xs: [
    { i: "kpi-open-inspections", x: 0, y: 0, w: 4, h: 4 },
    { i: "kpi-defects-this-month", x: 0, y: 4, w: 4, h: 4 },
    { i: "ongoing-projects", x: 0, y: 8, w: 4, h: 14 },
    { i: "latest-inspections", x: 0, y: 22, w: 4, h: 8 },
    { i: "recent-activity", x: 0, y: 30, w: 4, h: 8 },
    { i: "fleet-map", x: 0, y: 38, w: 4, h: 12 },
  ],
};

export const COLS = { lg: 12, md: 10, sm: 6, xs: 4 } as const;
// Mirror AnalysisWorkspace's calibration — measured RGL container width
// after the 272 px sidebar + 24 px main padding either side. See the
// long comment in AnalysisWorkspace.tsx for the math.
export const BREAKPOINTS = { lg: 880, md: 680, sm: 480, xs: 0 } as const;
export const ROW_HEIGHT = 30;
export const MARGIN: [number, number] = [8, 8];
export const DRAG_HANDLE_CLASS = "aero-dashboard-drag-handle";

export const LAYOUT_VERSION = 1 as const;

/**
 * Wire envelope per backend-requirement 019 §"Request body". The PUT
 * payload matches this shape verbatim; the GET response either matches
 * this shape (200) or returns `{ code: "not_found" }` (404).
 */
export interface DashboardLayoutEnvelope {
  version: typeof LAYOUT_VERSION;
  layouts: {
    lg: Layout[];
    md: Layout[];
    sm: Layout[];
    xs: Layout[];
  };
  hidden: Record<string, boolean>;
}

export function factoryEnvelope(): DashboardLayoutEnvelope {
  return {
    version: LAYOUT_VERSION,
    layouts: {
      lg: [...(DEFAULT_LAYOUTS.lg ?? [])],
      md: [...(DEFAULT_LAYOUTS.md ?? [])],
      sm: [...(DEFAULT_LAYOUTS.sm ?? [])],
      xs: [...(DEFAULT_LAYOUTS.xs ?? [])],
    },
    hidden: {},
  };
}
